import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Star, Upload, Loader2, Save } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RatingsUpdateDialog({ open, onOpenChange, stores, onSaved }) {
  const [rows, setRows] = useState([]);
  const [week, setWeek] = useState('');
  const [reading, setReading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setRows(stores.map(s => ({ store_id: s.id, store_name: s.name, current: s.rating ?? s.google_rating ?? null, rating: s.rating ?? s.google_rating ?? '' })));
      setWeek('');
    }
  }, [open, stores]);

  const readPdf = async (file) => {
    if (!file) return;
    setReading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await axios.post(`${API_URL}/store-ratings/from-pdf`, fd, { timeout: 120000 });
      const byId = Object.fromEntries(res.data.proposals.map(p => [p.store_id, p.rating]));
      setRows(prev => prev.map(r => byId[r.store_id] !== undefined ? { ...r, rating: byId[r.store_id], fromPdf: true } : r));
      if (res.data.week_ending) setWeek(res.data.week_ending);
      toast.success(`Read ${res.data.proposals.length} ratings from the newsletter — check them, then save`);
      if (res.data.unmatched?.length) toast.warning(`Couldn't match: ${res.data.unmatched.map(u => u.store).join(', ')}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not read the PDF'); }
    finally { setReading(false); }
  };

  const save = async () => {
    const items = rows.filter(r => r.rating !== '' && !isNaN(parseFloat(r.rating))).map(r => ({ store_id: r.store_id, rating: parseFloat(r.rating) }));
    if (!items.length) return toast.error('Enter at least one rating');
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/store-ratings`, { items, week_ending: week || null });
      toast.success(`Updated ${res.data.updated} store ratings`);
      onSaved?.(); onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="ratings-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Star className="w-5 h-5 text-amber-400 fill-amber-400" /> Update Google ratings</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <label className={`flex items-center justify-center gap-2 border-2 border-dashed rounded-lg p-4 cursor-pointer ${reading ? 'opacity-60' : 'hover:border-orange-300'}`} data-testid="ratings-pdf-drop">
            {reading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4 text-orange-500" />}
            <span>{reading ? 'Rooty is reading the newsletter…' : 'Drop the Weekly Operations Newsletter PDF to pre-fill'}</span>
            <input type="file" accept="application/pdf" className="hidden" disabled={reading} onChange={e => readPdf(e.target.files?.[0])} data-testid="ratings-pdf-input" />
          </label>
          <div className="flex items-center gap-2">
            <span className="text-slate-600 w-28">Week ending</span>
            <Input type="date" value={week} onChange={e => setWeek(e.target.value)} className="h-9 w-44" data-testid="ratings-week-input" />
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-slate-100 border rounded-lg">
            {rows.map(r => (
              <div key={r.store_id} className="flex items-center justify-between px-3 py-1.5" data-testid={`rating-row-${r.store_id}`}>
                <span className="text-slate-800">{r.store_name}<span className="text-xs text-slate-400 ml-2">now {r.current ?? '—'}</span></span>
                <Input type="number" step="0.1" min="0" max="5" value={r.rating} onChange={e => setRows(prev => prev.map(x => x.store_id === r.store_id ? { ...x, rating: e.target.value, fromPdf: false } : x))}
                  className={`h-8 w-20 text-right ${r.fromPdf ? 'border-orange-300 bg-orange-50' : ''}`} data-testid={`rating-input-${r.store_id}`} />
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">Values read from the PDF are highlighted — the chart is read by AI, so give them a quick glance before saving.</p>
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving || reading} className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm" data-testid="ratings-save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save ratings
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
